import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router';
import { Box, Divider, Typography, Skeleton } from '@mui/material';
import Grid from '@mui/material/Grid';

import { setOnCloseRedirectUrl } from 'components/user/localAuth';
import { LoadingTypography } from 'lib/LoadingTypography';
import { useAuth } from 'lib/useAuth';
import { useLocalHistory } from 'lib/useLocalHistory';
import { useStringReplace } from 'lib/useStringReplace';
import { LocationRouteParams, ROUTES } from 'pages/routes';
import { PayLinkButton } from '../ui/PayLinkButton';
import { PayAwardItem } from './PayAwardItem';
import { IAdjustmentLocalResource, IUpdateOrderAdjustment } from 'components/order/model/Order';
import { isOrderAvailableAdjastment } from 'components/order/helpers';
import { IAdjustmentReadResourceV22 } from 'components/basket/model/Basket';

const PREFIX = 'PayAwards';

const classes = {
    title: `${PREFIX}title`,
    linkText: `${PREFIX}linkText`
};

const StyledBox = styled(Box)(({ theme }) => ({
    [`& .${classes.title}`]: {
        marginBottom: theme.spacing(1),
        fontWeight: 700,
        fontSize: '18px'
    },

    [`& .${classes.linkText}`]: {
        fontWeight: 700,
        color: theme.palette.primary.main
    }
}));

interface PayAwardsProps {
    loading?: boolean;
    awards: IAdjustmentLocalResource[];
    selected?: IAdjustmentLocalResource[];
    onSelect: (awardId: string) => void;
    onUnselect: (awardId: string) => void;
    onClear: () => void;
    disabled?: boolean;
    showAddMoreButton?: boolean;
    onAddMore: (reward: IUpdateOrderAdjustment) => void;
    multi?: boolean;
}

export const PayAwards: React.FC<React.PropsWithChildren<PayAwardsProps>> = ({
    loading,
    awards,
    selected,
    onSelect,
    onUnselect,
    onClear,
    disabled,
    showAddMoreButton,
    onAddMore,
    multi
}) => {
    const { t } = useTranslation();
    const replace = useStringReplace();
    const { isGuest } = useAuth();
    const history = useLocalHistory();
    const { merchantId } = useParams<LocationRouteParams>();
    const location = useLocation();
    const selectedIds = React.useMemo(() => {
        const ids = new Set();
        if (Array.isArray(selected)) {
            selected.forEach(item => ids.add(item.awardId));
        }
        return ids;
    }, [selected]);
    const filteredAwards = React.useMemo<(IAdjustmentLocalResource | IAdjustmentReadResourceV22)[]>(() => {
        if (!Array.isArray(awards)) {
            return [];
        }
        if (multi && selected) {
            return [...selected, ...awards.filter(item => !selectedIds.has(item.awardId))].filter(item =>
                isOrderAvailableAdjastment(item)
            );
        }
        return awards.filter(item => isOrderAvailableAdjastment(item));
    }, [awards, multi, selected, selectedIds]);
    const handleClick = React.useCallback(
        (awardId: string) => {
            if (selectedIds.has(awardId)) {
                onUnselect(awardId);
            } else {
                onSelect(awardId);
            }
        },
        [onSelect, onUnselect, selectedIds]
    );
    const handleClear = React.useCallback(() => {
        onClear();
    }, [onClear]);
    const handleSignUpClick = React.useCallback(() => {
        if (merchantId) {
            setOnCloseRedirectUrl(`${location.pathname}${location.search}`, merchantId);
        }
        history.push(ROUTES.USER.REGISTER);
    }, [history, location.pathname, location.search, merchantId]);
    const handleAwardsClick = React.useCallback(() => {
        if (merchantId) {
            setOnCloseRedirectUrl(`${location.pathname}${location.search}`, merchantId);
        }
        history.push(ROUTES.USER.REWARDS);
    }, [history, location.pathname, location.search, merchantId]);
    const renderReward = React.useCallback(
        (reward: IAdjustmentLocalResource | IAdjustmentReadResourceV22) => (
            <PayAwardItem item={reward} key={reward.id} onClick={handleClick} />
        ),
        [handleClick]
    );
    const renderMultiReward = React.useCallback(
        (reward: IAdjustmentLocalResource | IAdjustmentReadResourceV22) => (
            <PayAwardItem
                item={reward}
                key={reward.id}
                onClick={handleClick}
                selected={selectedIds.has(reward.awardId)}
            />
        ),
        [handleClick, selectedIds]
    );
    const singleSelectedAward = React.useMemo(() => selected?.at(-1), [selected]);
    const handleAddMore = React.useCallback(() => {
        if (singleSelectedAward) {
            onAddMore({
                type: singleSelectedAward.type,
                awardId: singleSelectedAward.awardId,
                quantity: singleSelectedAward.quantity,
                selectedOn: singleSelectedAward.selectedOn
            });
        }
    }, [onAddMore, singleSelectedAward]);
    if (loading) {
        return (
            <StyledBox px={2} pb={1} pt={1}>
                <LoadingTypography loading width={100} className={classes.title} />
                <Box display="flex" justifyContent="space-between">
                    <LoadingTypography loading width={120} />
                    <Skeleton variant="circular" />
                </Box>
            </StyledBox>
        );
    }
    if (disabled) {
        return null;
    }
    if (isGuest) {
        return (
            <StyledBox px={2} pb={1} pt={1}>
                <LoadingTypography loading={loading} width={240}>
                    {replace('PAT_QUICKPAY_REWARDS_UNREGISTERED', 'link', index => (
                        <Typography
                            onClick={handleSignUpClick}
                            className={classes.linkText}
                            component="span"
                            key={`email-sent-${index}`}
                        >
                            {t('PAT_QUICKPAY_REWARDS_UNREGISTERED_LINK')}
                        </Typography>
                    ))}
                </LoadingTypography>
            </StyledBox>
        );
    }
    if (!singleSelectedAward && (!filteredAwards || !filteredAwards.length)) {
        return (
            <StyledBox px={2} pb={1} pt={1}>
                <LoadingTypography loading={loading} width={240}>
                    {replace('PAT_QUICKPAY_REWARDS_EMPTY', 'link', index => (
                        <Typography
                            onClick={handleAwardsClick}
                            className={classes.linkText}
                            component="span"
                            key={`email-sent-${index}`}
                        >
                            {t('PAT_QUICKPAY_REWARDS_EMPTY_LINK')}
                        </Typography>
                    ))}
                </LoadingTypography>
            </StyledBox>
        );
    }
    if (multi && showAddMoreButton) {
        return (
            <StyledBox px={2} pb={1} pt={1}>
                <Typography className={classes.title}>{t('BILL_CHECKOUT_AVAILABLE_REWARDS')}</Typography>
                <Box>{filteredAwards.map(renderMultiReward)}</Box>
                <Box pt={1}>
                    <Divider />
                </Box>
            </StyledBox>
        );
    }
    return (
        <StyledBox px={2} pb={1} pt={1}>
            <Typography className={classes.title}>{t('BILL_CHECKOUT_AVAILABLE_REWARDS')}</Typography>
            <Box>
                {!!singleSelectedAward && (
                    <PayAwardItem item={singleSelectedAward} selected onClick={handleClear} />
                )}
                {!singleSelectedAward && filteredAwards.map(renderReward)}
            </Box>
            {!!singleSelectedAward && (
                <Grid container justifyContent="space-between">
                    <Grid size={showAddMoreButton ? 6 : 12}>
                        <PayLinkButton onClick={handleClear}>
                            {t('PAT_QUICKPAY_REWARDS_CHANGE')}
                        </PayLinkButton>
                    </Grid>
                    {showAddMoreButton && (
                        <Grid size={6}>
                            <PayLinkButton onClick={handleAddMore}>
                                {t('PAT_QUICKPAY_SHOW_MORE_AWARDS')}
                            </PayLinkButton>
                        </Grid>
                    )}
                </Grid>
            )}
            <Box pt={1}>
                <Divider />
            </Box>
        </StyledBox>
    );
};
